import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import Swal from "sweetalert2";

export const generateReportPDF = async (printRef, HeadInfo, pid, tid) => {
  Swal.fire({
    title: 'Generating PDF...',
    html: 'Please wait while we prepare your report',
    allowOutsideClick: false,
    didOpen: () => Swal.showLoading(),
  });

  try {
    const element = printRef.current;
    if (!element) return;

    const buttons = document.querySelectorAll('.no-print');
    buttons.forEach(btn => btn.style.display = 'none');

    const header = element.querySelector('.print-header');
    const footer = element.querySelector('.pdf-footer');

    // Fix width for consistent rendering across all captures
    const origWidth = element.style.width;
    element.style.width = '800px';

    const captureOpts = {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
    };

    // Capture header and footer while both are still visible
    const headerCanvas = header ? await html2canvas(header, captureOpts) : null;
    const footerCanvas = footer ? await html2canvas(footer, captureOpts) : null;

    // Hide header and footer so main canvas contains only body content
    if (header) header.style.display = 'none';
    if (footer) footer.style.display = 'none';

    // canvasScale = html2canvas scale option (canvas px per screen px)
    const canvasScale = captureOpts.scale;
    const elTop = element.getBoundingClientRect().top;

    // Collect test and category box boundaries for split avoidance.
    // Tests are the primary unit — never split a test across pages.
    // Categories are the fallback — avoid splitting if the whole category fits.
    const collectBoxes = (selector) => {
      const boxes = [];
      element.querySelectorAll(selector).forEach(box => {
        const rect = box.getBoundingClientRect();
        boxes.push({
          top: Math.round(rect.top - elTop) * canvasScale,
          bottom: Math.round(rect.bottom - elTop) * canvasScale,
        });
      });
      return boxes;
    };
    const testBoxes = collectBoxes('.pdf-test');
    const categoryBoxes = collectBoxes('.pdf-category');

    // Row bottoms as fallback break points for boxes too tall to fit one page
    const rowBreaks = [0];
    element.querySelectorAll('tr').forEach(row => {
      rowBreaks.push(Math.round(row.getBoundingClientRect().bottom - elTop) * canvasScale);
    });
    rowBreaks.sort((a, b) => a - b);

    // Capture body content (no header, no footer)
    const canvas = await html2canvas(element, { ...captureOpts, width: 800 });

    // Restore DOM
    element.style.width = origWidth;
    buttons.forEach(btn => btn.style.display = '');
    if (header) header.style.display = '';
    if (footer) footer.style.display = '';

    const pdf = new jsPDF('p', 'mm', 'a4');
    const pdfW = pdf.internal.pageSize.getWidth();
    const pdfH = pdf.internal.pageSize.getHeight();
    const marginX = 10; // left/right margin in mm
    const marginY = 10; // top/bottom margin in mm
    const contentW = pdfW - marginX * 2;
    const pxPerMm = canvas.width / contentW;

    const headerMmH = headerCanvas ? (headerCanvas.height * contentW) / headerCanvas.width : 0;
    const footerMmH = footerCanvas ? (footerCanvas.height * contentW) / footerCanvas.width : 0;

    // Footer on every page so reserve its height from every page's content area
    const contentMmH = pdfH - marginY * 2 - headerMmH - footerMmH;
    const maxSlicePx = contentMmH * pxPerMm;

    // Each category always starts on a new page.
    // Within a category, never split a test; fall back to row-level if a test is too tall.
    const pages = [];
    const totalH = canvas.height;
    let currentY = 0;
    let targetEnd;

    // Closes over currentY and targetEnd (both var-hoisted to this scope)
    const findStraddlingTest = () => {
      for (const box of testBoxes) {
        if (box.top > currentY && box.top < targetEnd && box.bottom > targetEnd) {
          return box;
        }
      }
      return null;
    };

    for (let ci = 0; ci < categoryBoxes.length; ci++) {
      // First category starts at 0 (includes any leading margin); others start at box top
      currentY = ci === 0 ? 0 : categoryBoxes[ci].top;
      // Last category runs to end of canvas so trailing content (remarks etc.) is included
      const catEnd = ci < categoryBoxes.length - 1 ? categoryBoxes[ci].bottom : totalH;

      while (currentY < catEnd) {
        targetEnd = currentY + maxSlicePx;

        if (targetEnd >= catEnd) {
          pages.push({ start: currentY, end: catEnd });
          currentY = catEnd;
          break;
        }

        let bestBreak = targetEnd;

        // 1. Keep tests whole within this category
        const st = findStraddlingTest();
        if (st) {
          const h = st.bottom - st.top;
          if (h <= maxSlicePx && st.top > currentY + maxSlicePx * 0.1) {
            bestBreak = st.top;
          }
        }

        // 2. Row-level fallback for tests too tall to fit one page
        if (bestBreak === targetEnd) {
          for (let i = rowBreaks.length - 1; i >= 0; i--) {
            if (rowBreaks[i] > currentY && rowBreaks[i] <= targetEnd) {
              bestBreak = rowBreaks[i];
              break;
            }
          }
        }

        pages.push({ start: currentY, end: bestBreak });
        currentY = bestBreak;
      }
    }

    // Fallback: if no categories were found, paginate the whole canvas
    if (pages.length === 0) {
      currentY = 0;
      while (currentY < totalH) {
        targetEnd = currentY + maxSlicePx;
        if (targetEnd >= totalH) { pages.push({ start: currentY, end: totalH }); break; }
        let bestBreak = targetEnd;
        for (let i = rowBreaks.length - 1; i >= 0; i--) {
          if (rowBreaks[i] > currentY && rowBreaks[i] <= targetEnd) { bestBreak = rowBreaks[i]; break; }
        }
        pages.push({ start: currentY, end: bestBreak });
        currentY = bestBreak;
      }
    }

    const headerImg = headerCanvas ? headerCanvas.toDataURL('image/png') : null;
    const footerImg = footerCanvas ? footerCanvas.toDataURL('image/png') : null;
    const footerY = pdfH - marginY - footerMmH;

    for (let i = 0; i < pages.length; i++) {
      if (i > 0) pdf.addPage();

      const { start, end } = pages[i];
      const sliceH = end - start;

      // Header on every page
      if (headerImg) {
        pdf.addImage(headerImg, 'PNG', marginX, marginY, contentW, headerMmH);
      }

      // Extra gap between header and content on pages after the first
      const headerGap = i > 0 ? 5 : 0;

      // Content slice positioned below header
      const srcCanvas = document.createElement('canvas');
      srcCanvas.width = canvas.width;
      srcCanvas.height = sliceH;
      srcCanvas.getContext('2d').drawImage(
        canvas, 0, start, canvas.width, sliceH, 0, 0, canvas.width, sliceH
      );
      const sliceMmH = (sliceH * contentW) / canvas.width;
      pdf.addImage(srcCanvas.toDataURL('image/png'), 'PNG', marginX, marginY + headerMmH + headerGap, contentW, sliceMmH);

      // Footer on every page, pinned to bottom
      if (footerImg) {
        pdf.addImage(footerImg, 'PNG', marginX, footerY, contentW, footerMmH);
      }

      // Page number in the bottom margin
      pdf.setFontSize(9);
      pdf.setTextColor(108, 117, 125);
      pdf.text(`Page ${i + 1} of ${pages.length}`, pdfW - marginX, pdfH - 3, { align: 'right' });
      pdf.text('www.fohrabwah.org', marginX, pdfH - 3);
    }

    pdf.save(`FOH_Lab_Report_${pid}_${tid}.pdf`);
    Swal.close();
    Swal.fire({ icon: 'success', title: 'PDF Generated!', timer: 2000, showConfirmButton: false });
  } catch (error) {
    console.error("Error generating PDF:", error);
    Swal.close();
    Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to generate PDF.', confirmButtonText: 'OK' });
  }
};
